/**
 * Shared WebSocket connection manager for Console ↔ Cloud communication.
 *
 * All hooks (useLiveOrders, useTenantNotifications, useKdsMonitor) share
 * a SINGLE WebSocket connection through this manager. This eliminates:
 * - 3x redundant connections to the same endpoint
 * - 3x independent retry storms when the server is unreachable
 * - Connection counter leaks on the server side
 *
 * The manager uses ref-counting: the connection is established when the
 * first consumer calls acquire() and torn down when the last calls release().
 */
import type { ConsoleMessage, ConnectionState, ReconnectFailure } from '@/core/types/live';
import { PROTOCOL_ERROR_CODE } from '@/core/types/error-codes';
import { getValidWsToken } from '@/infrastructure/api/ws-token';
import { Protocol } from '@/generated/shared-constants';
import { parseConsoleMessage } from './consoleMessage';

const WS_BASE = import.meta.env.VITE_WS_BASE;
const RECONNECT_MIN_MS = 1000;
const RECONNECT_MAX_MS = 30_000;
const ORPHANED_HANDSHAKE_TIMEOUT_MS = 15_000;
export const MAX_ORPHANED_HANDSHAKES = 4;

type MessageListener = (msg: ConsoleMessage) => void;
type StateListener = (state: ConnectionState) => void;
type ReconnectFailureListener = (failure: ReconnectFailure) => void;

interface SocketReceipt {
  generation: number;
  socketId: number;
  ws: WebSocket;
}

interface ReconnectReceipt {
  generation: number;
  attemptId: number;
}

interface OrphanedHandshakeReceipt {
  socket: SocketReceipt;
  timeout: ReturnType<typeof setTimeout>;
}

class ConsoleWsManager {
  private currentSocket: SocketReceipt | null = null;
  private connectionState: ConnectionState = 'disconnected';
  private messageListeners = new Set<MessageListener>();
  private stateListeners = new Set<StateListener>();
  private reconnectFailureListeners = new Set<ReconnectFailureListener>();
  private reconnectDelay = RECONNECT_MIN_MS;
  private reconnectTimer: {
    generation: number;
    handle: ReturnType<typeof setTimeout>;
  } | null = null;
  private reconnectAttempt: ReconnectReceipt | null = null;
  private capacityBlockedGeneration: number | null = null;
  private connectionGeneration = 0;
  private nextSocketId = 0;
  private nextReconnectAttemptId = 0;
  private orphanedHandshakes = new Map<number, OrphanedHandshakeReceipt>();
  private refCount = 0;
  private token: string | null = null;
  private lastReady: ConsoleMessage | null = null;

  /** Call when a consumer hook mounts. Starts connection on first ref. */
  acquire(token: string): void {
    const tokenChanged = this.token !== null && this.token !== token;
    this.token = token;
    this.refCount++;
    if (this.refCount === 1) {
      const generation = ++this.connectionGeneration;
      this.reconnectDelay = RECONNECT_MIN_MS;
      this.connect(generation);
    } else if (tokenChanged) {
      const generation = ++this.connectionGeneration;
      this.reconnectDelay = RECONNECT_MIN_MS;
      this.disconnect();
      this.connect(generation);
    }
  }

  /** Call when a consumer hook unmounts. Disconnects when no refs remain. */
  release(): void {
    this.refCount--;
    if (this.refCount <= 0) {
      this.refCount = 0;
      this.connectionGeneration++;
      this.disconnect();
    }
  }

  getConnectionState(): ConnectionState {
    return this.connectionState;
  }

  /** Observable ownership receipt count; connect() keeps it bounded by MAX_ORPHANED_HANDSHAKES. */
  getOrphanedHandshakeCount(): number {
    return this.orphanedHandshakes.size;
  }

  /**
   * Subscribe to all ConsoleMessages. Returns unsubscribe function.
   * New subscribers immediately receive the last Ready message (if any)
   * so they don't miss the initial snapshot.
   */
  onMessage(listener: MessageListener): () => void {
    this.messageListeners.add(listener);
    if (this.lastReady) listener(this.lastReady);
    return () => { this.messageListeners.delete(listener); };
  }

  /**
   * Subscribe to connection state changes. Returns unsubscribe function.
   * Immediately fires with current state.
   */
  onStateChange(listener: StateListener): () => void {
    this.stateListeners.add(listener);
    listener(this.connectionState);
    return () => { this.stateListeners.delete(listener); };
  }

  /** Subscribe to token refresh failures observed by the reconnect owner. */
  onReconnectFailure(listener: ReconnectFailureListener): () => void {
    this.reconnectFailureListeners.add(listener);
    return () => { this.reconnectFailureListeners.delete(listener); };
  }

  /** Send a command through the shared connection. */
  send(cmd: object): void {
    const current = this.currentSocket;
    if (current && this.isCurrentSocket(current) && current.ws.readyState === WebSocket.OPEN) {
      current.ws.send(JSON.stringify(cmd));
    }
  }

  // ── Private ──────────────────────────────────────────────

  private connect(generation: number): void {
    if (!this.isActiveGeneration(generation) || !this.token || this.currentSocket) return;
    const token = this.token;
    const remainsCurrent = () => (
      this.isActiveGeneration(generation) &&
      this.token === token &&
      !this.currentSocket
    );
    if (!this.setConnectionState('connecting', remainsCurrent)) return;
    if (this.orphanedHandshakes.size >= MAX_ORPHANED_HANDSHAKES) {
      this.capacityBlockedGeneration = generation;
      return;
    }
    this.capacityBlockedGeneration = null;

    // RT-NH3: JWT 通过 Sec-WebSocket-Protocol header 传递,不再放 query string。
    // 浏览器 WebSocket API 不支持自定义 headers,但 subprotocols 数组会编码成
    // `Sec-WebSocket-Protocol` 同时携带 bearer token 和 shared console protocol。
    // Server 解析 bearer.<JWT> 验证身份,echo 回 protocol 满足 RFC 协议协商。
    // Query string 会被 Caddy/nginx access log 记录 → JWT 泄漏;header 默认不入 log。
    const url = `${WS_BASE}/api/tenant/live-orders/ws`;
    const ws = new WebSocket(url, [`bearer.${token}`, Protocol.CONSOLE_WS_PROTOCOL]);
    const receipt: SocketReceipt = {
      generation,
      socketId: ++this.nextSocketId,
      ws,
    };
    this.currentSocket = receipt;

    ws.onopen = () => {
      if (!this.isCurrentSocket(receipt)) return;
      this.reconnectDelay = RECONNECT_MIN_MS;
      if (!this.setConnectionState(
        'connected',
        () => this.isCurrentSocket(receipt),
      )) return;
      // Subscribe to ALL stores; each hook filters client-side by store_id
      ws.send(JSON.stringify({ type: 'Subscribe', store_ids: [] }));
    };

    ws.onmessage = (event) => {
      if (!this.isCurrentSocket(receipt)) return;
      const msg = this.parseMessage(event.data);
      if (!msg) {
        ws.close(1002, 'protocol_error');
        return;
      }
      if (msg.type === 'Ready') this.lastReady = msg;
      for (const listener of this.messageListeners) {
        listener(msg);
      }
    };

    ws.onclose = () => {
      if (!this.isCurrentSocket(receipt)) return;
      this.currentSocket = null;
      this.setConnectionState('reconnecting');
      this.scheduleReconnect(generation);
    };

    ws.onerror = () => {
      if (!this.isCurrentSocket(receipt)) return;
      if (ws.readyState === WebSocket.OPEN) ws.close();
    };
  }

  private scheduleReconnect(generation: number): void {
    if (
      !this.isActiveGeneration(generation) ||
      this.reconnectTimer ||
      this.reconnectAttempt ||
      this.currentSocket
    ) return;

    const delay = this.reconnectDelay;
    this.reconnectDelay = Math.min(this.reconnectDelay * 2, RECONNECT_MAX_MS);
    const handle = setTimeout(() => {
      if (
        this.reconnectTimer?.generation !== generation ||
        this.reconnectTimer.handle !== handle
      ) return;
      this.reconnectTimer = null;
      void this.refreshAndReconnect(generation);
    }, delay);
    this.reconnectTimer = { generation, handle };
  }

  private async refreshAndReconnect(generation: number): Promise<void> {
    if (!this.isActiveGeneration(generation) || this.reconnectAttempt || this.currentSocket) return;

    const receipt: ReconnectReceipt = {
      generation,
      attemptId: ++this.nextReconnectAttemptId,
    };
    this.reconnectAttempt = receipt;

    try {
      const freshToken = await getValidWsToken();
      if (!this.isCurrentReconnect(receipt)) return;
      this.reconnectAttempt = null;
      if (!freshToken) {
        this.publishReconnectFailure({ kind: 'token_unavailable' });
        this.setConnectionState('disconnected');
        return;
      }
      this.token = freshToken;
      this.connect(generation);
    } catch (error) {
      if (!this.isCurrentReconnect(receipt)) return;
      this.reconnectAttempt = null;
      if (!this.isActiveGeneration(generation)) return;
      const refreshError = error instanceof Error ? error : new Error(String(error));
      this.publishReconnectFailure({ kind: 'token_refresh_failed', error: refreshError });
      this.setConnectionState('reconnecting');
      this.scheduleReconnect(generation);
    }
  }

  private disconnect(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer.handle);
      this.reconnectTimer = null;
    }
    this.reconnectAttempt = null;
    this.capacityBlockedGeneration = null;

    if (this.currentSocket) {
      const receipt = this.currentSocket;
      const { ws } = receipt;
      this.currentSocket = null;
      ws.onmessage = null;

      if (ws.readyState === WebSocket.CONNECTING) {
        // Calling close() while the browser handshake is still in flight emits
        // "WebSocket is closed before the connection is established". Detach
        // the manager immediately, then close cleanly if the handshake wins.
        this.adoptOrphanedHandshake(receipt);
      } else {
        ws.onopen = null;
        ws.onerror = null;
        ws.onclose = null;
        if (ws.readyState === WebSocket.OPEN) {
          ws.close(1000, 'unused');
        }
      }
    }
    this.lastReady = null;
    this.setConnectionState('disconnected');
  }

  private adoptOrphanedHandshake(receipt: SocketReceipt): void {
    const { socketId, ws } = receipt;
    const retire = () => this.retireOrphanedHandshake(socketId);
    ws.onopen = () => ws.close(1000, 'unused');
    ws.onerror = retire;
    ws.onclose = retire;

    const timeout = setTimeout(() => {
      const orphan = this.orphanedHandshakes.get(socketId);
      if (!orphan || orphan.socket.ws !== ws) return;

      ws.onopen = null;
      ws.onerror = null;
      ws.onclose = null;
      if (
        ws.readyState === WebSocket.CONNECTING ||
        ws.readyState === WebSocket.OPEN
      ) {
        ws.close(1000, 'handshake_timeout');
      }
      this.retireOrphanedHandshake(socketId);
    }, ORPHANED_HANDSHAKE_TIMEOUT_MS);
    this.orphanedHandshakes.set(socketId, { socket: receipt, timeout });
  }

  private retireOrphanedHandshake(socketId: number): void {
    const orphan = this.orphanedHandshakes.get(socketId);
    if (!orphan) return;
    clearTimeout(orphan.timeout);
    this.orphanedHandshakes.delete(socketId);
    const generation = this.capacityBlockedGeneration;
    if (
      generation !== null &&
      this.isActiveGeneration(generation) &&
      !this.currentSocket &&
      !this.reconnectTimer &&
      !this.reconnectAttempt
    ) {
      this.connect(generation);
    }
  }

  private isActiveGeneration(generation: number): boolean {
    return this.refCount > 0 && this.connectionGeneration === generation;
  }

  private isCurrentSocket(receipt: SocketReceipt): boolean {
    return (
      this.isActiveGeneration(receipt.generation) &&
      this.currentSocket?.socketId === receipt.socketId &&
      this.currentSocket.ws === receipt.ws
    );
  }

  private isCurrentReconnect(receipt: ReconnectReceipt): boolean {
    return (
      this.isActiveGeneration(receipt.generation) &&
      this.reconnectAttempt?.generation === receipt.generation &&
      this.reconnectAttempt.attemptId === receipt.attemptId
    );
  }

  private setConnectionState(
    state: ConnectionState,
    remainsCurrent?: () => boolean,
  ): boolean {
    this.connectionState = state;
    const notificationStillCurrent = () => (
      this.connectionState === state &&
      (!remainsCurrent || remainsCurrent())
    );
    for (const listener of [...this.stateListeners]) {
      if (!this.stateListeners.has(listener)) continue;
      listener(state);
      if (!notificationStillCurrent()) return false;
    }
    return notificationStillCurrent();
  }

  private publishReconnectFailure(failure: ReconnectFailure): void {
    for (const listener of [...this.reconnectFailureListeners]) {
      if (!this.reconnectFailureListeners.has(listener)) continue;
      try {
        listener(failure);
      } catch {
        // Observers cannot interrupt reconnect ownership.
      }
    }
  }

  private parseMessage(data: unknown): ConsoleMessage | null {
    if (typeof data !== 'string') {
      this.emitProtocolError('Console WebSocket received a non-text frame');
      return null;
    }

    let parsed: unknown;
    try {
      parsed = JSON.parse(data);
    } catch (err) {
      const suffix = err instanceof Error ? `: ${err.message}` : '';
      this.emitProtocolError(`Console WebSocket received malformed JSON${suffix}`);
      return null;
    }

    try {
      return parseConsoleMessage(parsed);
    } catch (err) {
      const suffix = err instanceof Error ? `: ${err.message}` : '';
      this.emitProtocolError(`Console WebSocket message failed protocol validation${suffix}`);
      return null;
    }
  }

  private emitProtocolError(message: string): void {
    const msg: ConsoleMessage = {
      type: 'ProtocolError',
      code: PROTOCOL_ERROR_CODE,
      message,
    };
    for (const listener of this.messageListeners) {
      listener(msg);
    }
  }
}

export const consoleWs = new ConsoleWsManager();
